import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { applicationsDropdown } from '@app/constants/applications-dropdown';
import { ProductSubtypeEnum } from '@app/models/enums/product-subtype.enum';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { ApplicationInterface } from '@app/models/interfaces/application.interface';
import { FilterSearchRequestInterface } from '@app/models/requests/filter-search-request.interface';
import { ProductTypeInterface, TaqmanGlobalSearchResponseInterface } from '@app/models/responses/taqman-search-response.interface';
import { DropdownOption } from '@app/modules/tf-dropdowns/model/DropdownOption';
import { TfTypeaheadService } from '@app/modules/tf-typeahead/services/typeahead.service';
import { ProductSiteRegionService } from '@app/services/product-site-region.service';
import { TfApiService } from '@app/services/api.service';
import { StoreService } from '@app/services/store.service';
import { TfWindowRefService } from '@app/modules/tf-window-ref';
import { debounceTime, distinctUntilChanged, Observable, OperatorFunction, Subscription, switchMap } from 'rxjs';

@Component({
    selector: 'tf-search-header',
    templateUrl: './search-header.component.html',
    styleUrls: ['./search-header.component.scss']
})
export class TfSearchHeaderComponent implements OnInit {

    @Input()
    productType: ProductTypeEnum | null = null;

    @Input()
    productSubtype: ProductSubtypeEnum | null = null;

    @Output() searchResult = new EventEmitter();

    searchValue: string = '';
    keyword: string;
    searchBtnClicked = false
    showTitle: boolean = true;
    singleSelectedVal: ApplicationInterface | null = null;

    names = ['TaqMan Assays', 'Assays', 'Gene Expression', 'Microbe & ABR Detection', 'SNP Genotyping', 'Copy Number', 'miRNA', 'Mutation Detection', 'Arrays', 'Gene Expression', 'miRNA'];
    applications: ApplicationInterface[] = JSON.parse(JSON.stringify(applicationsDropdown));

    productSearch: string = null;
    subscriptions: Subscription = new Subscription();
    displaySpecies: boolean = false;
    speciesList: DropdownOption[] = [];
    selectedSpecies: any = [];
    isFisherSci: boolean = false;

    constructor(
        private router: Router,
        private windowRef: TfWindowRefService,
        private storeService: StoreService,
        private route: ActivatedRoute,
        private typeaheadService: TfTypeaheadService,
        private apiService: TfApiService,
        private productSiteRegionService: ProductSiteRegionService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {

        if (this.productSiteRegionService.isFisherSci()) {
            this.isFisherSci = true;

            const applicationsToOmitFS: string[] = ['mb-abr-assay', 'md-assay'];
            this.applications = this.applications.filter(app => !applicationsToOmitFS.includes(app.id))
            this.names = ['TaqMan Assays', 'Assays', 'Gene Expression', 'SNP Genotyping', 'Copy Number', 'miRNA', 'Arrays', 'Gene Expression', 'miRNA'];
        }

        this.router.routeReuseStrategy.shouldReuseRoute = () => false;

        if (this.route.parent.snapshot.routeConfig.path.toLowerCase().indexOf('results') >= 0) {
            this.displaySpecies = true;
        }

        this.searchValue = this.storeService.getKeyword();

        if (this.searchValue == '') {
            this.searchValue = '*';
        }

        if(this.productType === null && this.route.snapshot.queryParams['productType']) {
            this.productType = this.route.snapshot.queryParams['productType'];
        }

        if(this.productSubtype === null && this.route.snapshot.queryParams['productSubtype']) {
            this.productSubtype = this.route.snapshot.queryParams['productSubtype'];
        }

        this.subscribeToProductSearchResultCount();
        this.setInitialValueOnDropdown();

    }

    @HostListener('window:scroll', ['$event'])
    handleScroll() {
        this.showTitle = this.windowRef.nativeWindow.scrollY < 50;
    }

    getApplicationInlineLabel() {
        return this.displaySpecies ? 'Application (Recommended)' : 'Application';
    }

    inputOnFocusEvent() {
        if (this.searchValue == '*') {
            this.searchValue = '';
        }
    }

    setInitialValueOnDropdown(): void {

        const application = this.applications.find((application) => {
            return application.productType === this.productType && application.productSubType.includes(this.productSubtype);
        });

        if (application) {
            this.singleSelectedVal = application;
        }

    }

    subscribeToProductSearchResultCount(): void {

        const subscription = this.storeService.globalSearchResults$.subscribe((data) => {
            this.updateLabel(data);
        });

        this.subscriptions.add(subscription);

    }

    setSpeciesOpts() {

        this.speciesList = [];
        this.selectedSpecies = [];
        if (this.displaySpecies) {
            const filterSearchParams: FilterSearchRequestInterface = {
                searchKeyword: this.searchValue,
                filterToSearch: 'Species',
                typeAheadKeyword: '*',
                productType: this.singleSelectedVal.id,
                productSubtype: this.singleSelectedVal.id
            }
            this.apiService.filterSearch(filterSearchParams).subscribe(
                {
                    next: (response) => {
                        response.forEach(el => {
                            this.speciesList.push({
                                name: el.name,
                                value: el,
                                isChecked: false
                            })
                        });
                    },
                    error: (err) => {
                        console.log('Error', err);
                    }
                }
            );

        }
    }

    isSpeciesDisabled() {
        return this.speciesList.length === 0;
    }

    updateLabel(dropdownCounts: TaqmanGlobalSearchResponseInterface) {
        if (dropdownCounts && dropdownCounts.productTypeHits) {
            const productsCount = dropdownCounts.productTypeHits;
            this.resetNames(this.names, this.applications);
            this.validateCounts(productsCount);
            this.applications = [...this.applications];
            this.singleSelectedVal = this.applications.find((application) => {
                return application.id === this.singleSelectedVal?.id;
            });
        }
    }

    resetNames(names: string[], label: ApplicationInterface[]) {
        if (names && label) {
            for (let i = 0; i < label.length; i++) {
                label[i].name = names[i];
                label[i].disabled = false;
            }
        }
    }

    validateCounts(productsCount: ProductTypeInterface) {

        this.storeService.setSearchResultCounts({
            assayCount: productsCount.TAQMAN_GE || 0,
            arrayCount: productsCount.ARRAY_GX || 0
        });

        this.applications.forEach(data => {
            if (data.id) {
                let newName = data.name;
                let totalCount = 0;
                if (data.searchType === 'array') {
                    switch (data.id) {
                        case 'gx':
                            newName = productsCount.ARRAY_GX ? data.name + ' (' + productsCount.ARRAY_GX.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'sg-array':
                            newName = productsCount.ARRAY_GT ? data.name + ' (' + productsCount.ARRAY_GT.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'mr-array':
                            newName = productsCount.ARRAY_MIRNA ? data.name + ' (' + productsCount.ARRAY_MIRNA.toLocaleString('en-US') + ')' : data.name;
                            break;
                    }
                }
                if (data.searchType === 'assay') {
                    switch (data.id) {
                        case 'ge':
                            newName = productsCount.TAQMAN_GE ? data.name + ' (' + productsCount.TAQMAN_GE.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'sg-assay':
                            newName = productsCount.GT ? data.name + ' (' + productsCount.GT.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'cn-assay':
                            newName = productsCount.CNV ? data.name + ' (' + productsCount.CNV.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'mr-assay':
                            totalCount = (productsCount.ADVANCED_MIRNA | 0) + (productsCount.MIRNA_CONTROL_ASSAY | 0) + (productsCount.PRI_MIRNA | 0);
                            newName = totalCount > 0 ? data.name + ' (' + totalCount.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'md-assay':
                            totalCount = (productsCount.DPCR | 0) + (productsCount.CPCR | 0);
                            newName = totalCount > 0 ? data.name + ' (' + totalCount.toLocaleString('en-US') + ')' : data.name;
                            break;
                        case 'mb-abr-assay':
                            totalCount = (productsCount.MICROBE_DETECTION | 0) + (productsCount.ANTIBIOTIC_RESISTANCE | 0);
                            newName = totalCount > 0 ? data.name + ' (' + totalCount.toLocaleString('en-US') + ')' : data.name;
                            break;
                    }
                }
                if (data.searchType !== 'apps') {
                    data.disabled = newName === data.name;
                    data.name = newName;
                }

            }
        });
    }

    removeSelectedSpecification() {
        this.resetNames(this.names, this.applications);
        this.searchValue = '';
    }

    doSearch(): void {

        this.storeService.setFilterPillState();
        this.storeService.setSearchValue(this.searchValue);

        if (this.singleSelectedVal === null || this.singleSelectedVal.id === '') {

            void this.router.navigate(['results'], {
                queryParams: {
                    keyword: this.searchValue,
                    productType: this.singleSelectedVal?.searchType || null
                }
            });

            return;

        }

        if (this.singleSelectedVal?.redirectionUrl) {
            const baseUrl = window.location.origin;
            let path = this.singleSelectedVal.redirectionUrl.replace('{{keyword}}', this.searchValue);
            if (this.selectedSpecies.length > 0) {
                path = path + '&species=' + this.selectedSpecies.map(el => el.value.queryValue).join(',');
            }
            window.location.href = `${baseUrl}${path}`;
        } else {

            const application = applicationsDropdown.find((product) => {
                return product.id === this.singleSelectedVal.id;
            });

            if( application.showInUnifiedCard ) {

                this.storeService.resetGlobalSearchResults();
                this.storeService.setSearchValue(this.searchValue);

                void this.router.navigate(['results'], {
                    queryParams: {
                        keyword: this.searchValue,
                        productSearch: application.unifiedCardProductSearch
                    }
                });

                return;

            }


            const queryParams = {
                keyword: this.searchValue,
                productType: this.singleSelectedVal.productTypeURLParam,
                productSubtype: this.singleSelectedVal.productSubTypeURLParam
            }

            if (this.selectedSpecies.length > 0) {
                queryParams['filters'] = 'Species:' + this.selectedSpecies.map(el => el.value.queryValue).join(',') + '~';
            }

            const modulePath = application?.modulePath || "results";
            void this.router.navigate([modulePath], {queryParams});

        }

    }

    openModal() {
        this.searchBtnClicked = !this.searchBtnClicked;
    }

    resetCounts() {
        this.resetNames(this.names, this.applications);
    }

    compare(a: ApplicationInterface, b: ApplicationInterface): boolean {
        return a?.id === b?.id;
    }

    search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => {
        return text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((term) => {
                const productTypeToSearch = this.singleSelectedVal?.productType || 'taqman';
                return this.typeaheadService.getSuggestions(term, productTypeToSearch);
            }));

    }

    onKeyPressHandler($event: KeyboardEvent) {

        if ($event.code === 'Enter' || $event.code === 'NumpadEnter') {
            this.doSearch();
        }

    }

}
